/**
 * Error Pattern Matcher
 * Parses error messages to provide contextual, actionable suggestions
 * and sanitized technical details (without exposing sensitive system info).
 */

const ERROR_PATTERNS = [
  {
    id: "timeout",
    patterns: [/timeout/i, /timed?\s*out/i, /ETIMEDOUT/i, /deadline\s*exceeded/i],
    suggestions: ["Try again in a moment"],
    technicalDetail: "Request timed out",
  },
  {
    id: "connection_reset",
    patterns: [/ECONNRESET/i, /connection reset/i],
    suggestions: ["The connection was interrupted. Please try again."],
    technicalDetail: "Connection reset by peer",
  },
  {
    id: "host_unreachable",
    patterns: [/EHOSTUNREACH/i, /host unreachable/i],
    suggestions: ["Unable to reach the server. Please check your connection."],
    technicalDetail: "Host unreachable",
  },
  {
    id: "dns_failure",
    patterns: [/EAI_AGAIN/i, /getaddrinfo/i],
    suggestions: ["DNS lookup failed. Please check your internet connection."],
    technicalDetail: "DNS resolution failed",
  },
  {
    id: "fetch_failed",
    patterns: [/fetch failed/i, /failed to fetch/i, /network request failed/i],
    suggestions: ["Network request failed. Please check your connection and try again."],
    technicalDetail: "Fetch operation failed",
  },
  {
    id: "server_error_500",
    patterns: [/\b500\b/, /internal\s*server\s*error/i],
    suggestions: ["Try a different model - this one may be temporarily unavailable"],
    technicalDetail: "HTTP 500 - Internal Server Error",
  },
  {
    id: "server_error_502",
    patterns: [/\b502\b/, /bad\s*gateway/i],
    suggestions: ["Try a different model - this one may be temporarily unavailable"],
    technicalDetail: "HTTP 502 - Bad Gateway",
  },
  {
    id: "server_error_503",
    patterns: [/\b503\b/, /service\s*unavailable/i, /overloaded/i],
    suggestions: ["Try a different model - this one may be temporarily unavailable"],
    technicalDetail: "HTTP 503 - Service Unavailable",
  },
  {
    id: "server_error_504",
    patterns: [/\b504\b/, /gateway\s*timeout/i],
    suggestions: ["Try again in a moment"],
    technicalDetail: "HTTP 504 - Gateway Timeout",
  },
  {
    id: "authentication",
    patterns: [
      /\b401\b/,
      /\b403\b/,
      /unauthorized/i,
      /credentials/i,
      /api\s*key/i,
      /not\s*(?:configured|set\s*up|set)/i,
      /authentication/i,
    ],
    suggestions: ["This provider may not be configured - try a different model"],
    technicalDetail: "Authentication failed",
  },
  {
    id: "rate_limit",
    patterns: [
      /\b429\b/,
      /rate\s*limit/i,
      /too\s*many\s*requests/i,
      /throttl/i,
    ],
    suggestions: ["Wait a few minutes or try a different provider"],
    technicalDetail: "Rate limit exceeded",
  },
  {
    id: "content_policy",
    patterns: [
      /content\s*policy/i,
      /safety/i,
      /flagged/i,
      /blocked/i,
      /moderation/i,
      /violat/i,
    ],
    suggestions: ["Try rephrasing your prompt"],
    technicalDetail: "Content policy violation",
  },
  {
    id: "network",
    patterns: [
      /ECONNREFUSED/i,
      /ECONNRESET/i,
      /ENOTFOUND/i,
      /fetch\s*failed/i,
      /network\s*error/i,
      /connection.*(?:failed|refused|reset)/i,
    ],
    suggestions: ["Check your connection and try again"],
    technicalDetail: "Network connection failed",
  },
  {
    id: "model_not_found",
    patterns: [
      /model.*not\s*(?:found|available|exist)/i,
      /invalid\s*model/i,
      /unknown\s*model/i,
      /\b404\b/,
    ],
    suggestions: ["This model may not be available - try a different one"],
    technicalDetail: "Model not found",
  },
  {
    id: "quota",
    patterns: [
      /quota/i,
      /billing/i,
      /limit\s*reached/i,
      /usage\s*limit/i,
      /insufficient.*(?:funds|credits)/i,
    ],
    suggestions: ["Usage limit reached - try a different provider"],
    technicalDetail: "Usage quota exceeded",
  },
  {
    id: "context_length",
    patterns: [
      /context\s*length/i,
      /too\s*long/i,
      /token\s*limit/i,
      /maximum.*length/i,
    ],
    suggestions: ["Your message is too long - try shortening it"],
    technicalDetail: "Context length exceeded",
  },
];

const DEFAULT_SUGGESTIONS = ["Try again", "Try a different model"];
const DEFAULT_TECHNICAL_DETAIL = "An error occurred";

/**
 * Parse error message and return suggestions, sanitized technical detail, and pattern ID
 * @param {string} errorMessage - The error message to parse
 * @returns {{ suggestions: string[], technicalDetail: string, patternId: string|null }}
 */
export function getErrorInfo(errorMessage) {
  if (!errorMessage || typeof errorMessage !== "string") {
    return {
      suggestions: DEFAULT_SUGGESTIONS,
      technicalDetail: DEFAULT_TECHNICAL_DETAIL,
      patternId: null,
    };
  }

  for (const pattern of ERROR_PATTERNS) {
    for (const regex of pattern.patterns) {
      if (regex.test(errorMessage)) {
        return {
          suggestions: pattern.suggestions,
          technicalDetail: pattern.technicalDetail,
          patternId: pattern.id,
        };
      }
    }
  }

  return {
    suggestions: DEFAULT_SUGGESTIONS,
    technicalDetail: DEFAULT_TECHNICAL_DETAIL,
    patternId: null,
  };
}

/**
 * @deprecated Use getErrorInfo instead
 */
export function getErrorSuggestions(errorMessage) {
  return getErrorInfo(errorMessage).suggestions;
}

export { ERROR_PATTERNS, DEFAULT_SUGGESTIONS, DEFAULT_TECHNICAL_DETAIL };
